import Hashes from "jshashes";
import React, { useEffect, useState, memo, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  Card,
  Typography,
  CardMedia,
  Tooltip,
  useTheme,
  Box,
} from "@mui/material";
import Stack from "@mui/material/Stack";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

import {
  setBookmarkedCollectionIds,
  setCollectionId,
  setCollectionSourceId,
  setEntityName,
  setIsCitationAvailable,
  setIsSidebarOpen,
  setIsSourceSidebarOpen,
  setIsSummaryAvailable,
  setLevel,
  setOthersNeeded,
  setReadingLevelAvailable,
  setSelectedBookmarkOption,
  setSourceUrl,
  setSummaryInfoData,
} from "context/features/dataSlice";
import defaultBackground from "assets/images/image-placeholder.png";
import { ReactComponent as EbscoLogo } from "assets/icons/EBSCO_Information_Services_20xx_logo.svg";
import proQuestLogo from "assets/icons/proquest-logo.svg";

import { ReactComponent as CollectionsLogo } from "assets/icons/bookmark-blue-icon.svg";
import { ReactComponent as CollectionsLogoHover } from "assets/icons/bookmark-hover-icon.svg";
import { HiOutlineLockClosed } from "react-icons/hi2";
import { ImInfo } from "react-icons/im";
import NewsguardScore from "components/base/NewsguardScore";
import AllSidesRating from "components/base/all-sides/AllSidesRating";
import { logEvent } from "services/amplitude.service.js";
import CharityNavigatorScore from "components/base/CharityNavigatorScore";
// import { useIsTab } from "hooks/is-tab.hook";
import { useIsMobile } from "hooks/use-breakpoint-checks.hook";
import SearchResultLink from "features/SourceSearch/components/SearchResultLink";
import FileTypeIcon from "components/base/FileTypeIcon";
import { devViewPdf } from "config";
import { showNewsguard } from "utils/show-ratings";
// import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import OpenAccessChip from "features/CategoryNews/component/open-access-chip";
import SmileyIcon from "./smiley-icon";
import { useSwiper, useSwiperSlide } from "swiper/react";
import AccessibleIconButton from "components/base/AccessibleIconButton";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const NewsCardComponent = ({ data, query, category, slideIndex }) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  // const isTab = useIsTab();
  const dispatch = useDispatch();

  const hashId = useMemo(() => {
    return new Hashes.MD5().hex(data.url);
  }, [data]);

  const isDatabaseProvider = ["ebsco", "proquest"].includes(
    String(category).toLowerCase()
  );
  const isEbsco = String(category).toLowerCase() === "ebsco";
  const hasPublisherInfo = !isDatabaseProvider && data.hostName != null;

  const openSourceSidebar = (tour = false) => {
    if (isDatabaseProvider) return;
    if (!tour) {
      logEvent("click_card_header", {
        host: data.hostName,
        entity: data.entityName,
      });
    }
    dispatch(setOthersNeeded(true));
    dispatch(setIsSourceSidebarOpen(true));
    dispatch(setSourceUrl(data.hostName));
    dispatch(setEntityName(data.entityName));
  };

  const DatabaseDetails = () => {
    const authors = Array.isArray(data.authors)
      ? data.authors.filter((author) => typeof author === "string" && author.trim())
      : [];
    const source = data.sourceTitle || data.entityName;
    const edition = [
      data.volume && `Vol. ${data.volume}`,
      data.issue && `Issue ${data.issue}`,
    ].filter(Boolean).join(", ");
    const database = data.dbLabel || data.dbId;
    const wordCount = data.wordCount;
    const hasWordCount = typeof wordCount === "number" && Number.isInteger(wordCount) && wordCount >= 0;
    const badgeSx = {
      px: 0.75, py: 0.25, borderRadius: 0.5, bgcolor: "action.hover",
      typography: "body2", fontWeight: 600, display: "inline-flex",
      alignItems: "center", gap: 0.5,
    };


    return (
      <Stack gap={0.75} sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
        {authors.length > 0 && (
          <Tooltip title={authors.join("; ")} enterDelay={750} followCursor placement="bottom-start">
            <Typography
              variant="body2"
              noWrap
              sx={{ minWidth: 0 }}
            >
              <strong>{authors.length === 1 ? "Author:" : "Authors:"}</strong>{" "}
              {authors.join("; ")}
            </Typography>
          </Tooltip>
        )}
        {(source || edition || data.isPeerReviewed === true) && (
          <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
            {(source || edition) && (
              <Tooltip title={[source, edition].filter(Boolean).join(" ")} enterDelay={750} placement="bottom-start">
                <Typography variant="body2" noWrap sx={{ minWidth: 0, maxWidth: "100%" }}>
                  <strong>From:</strong>{" "}
                  {source}
                  {edition && ` (${edition})`}
                </Typography>
              </Tooltip>
            )}
            {data.isPeerReviewed === true && (
              <Typography variant="body2" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                <CheckCircleIcon sx={{ fontSize: 16 }} />Peer-Reviewed
              </Typography>
            )}
          </Stack>
        )}
        <Stack direction="row" flexWrap="wrap" gap={0.75} alignItems="center" sx={{ mt: 1 }}>
          {hasWordCount && <Box component="span" sx={badgeSx}>{wordCount.toLocaleString("en-US")} words</Box>}
          {data.docType && <Box component="span" sx={badgeSx}>{data.docType}</Box>}
          {data.hasPdf === true && (
            <Box component="span" sx={badgeSx} aria-label="PDF available">
              <PictureAsPdfOutlinedIcon sx={{ fontSize: 16 }} />PDF
            </Box>
          )}
          {data.hasOnlineFullText === true && (
            <Box component="span" sx={badgeSx} aria-label="Online full text available">
              <ArticleOutlinedIcon sx={{ fontSize: 16 }} />Online full text
            </Box>
          )}
        </Stack>
        {database && <Typography variant="body2" color="text.secondary">Database: {database}</Typography>}
      </Stack>
    );
  };

  const CardHeader = () => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
        onClick={isDatabaseProvider ? undefined : openSourceSidebar}
      sx={{
        borderRadius: 1,
        px: "1rem",
        py: 0.4,
        cursor: hasPublisherInfo ? "pointer" : "default",
        ...(hasPublisherInfo && {
          "&:hover": { backgroundColor: "secondary.main" },
        }),
      }}
    >
      <Stack direction="row" alignItems="center" gap={0.75} minWidth={0}>
        <Tooltip
          enterDelay={750}
          followCursor
          title={data.entityName}
          placement="bottom-start"
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: "bold",
              width: "20rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "grey.600",
            }}
          >
            {data.entityName}
          </Typography>
        </Tooltip>
      </Stack>
      <Stack direction="row" alignItems="center" gap={2}>
        {data.hasOpenAccess &&
          category === "Academia & Scholarly Resources" && <OpenAccessChip />}
        {data.mediaBiasRating && (
          <AllSidesRating
            size="small"
            single
            activeCategory={data.mediaBiasRating}
            className={category === "Global News" ? "tour-all-sides-icon" : ""}
            hash={hashId}
          />
        )}
        {showNewsguard(data.score) && (
          <Box
            className={category === "Global News" ? "tour-newsguard-icon" : ""}
            data-article-id={hashId}
          >
            <NewsguardScore score={Number(data.score)} size="small" />
          </Box>
        )}
        {data.charityNavigatorScore &&
          category !== "Academia & Scholarly Resources" &&
          !data.score &&
          !data.mediaBiasRating && (
            <Box className="tour-charity-icon" data-article-id={hashId}>
              <CharityNavigatorScore
                score={data.charityNavigatorScore}
                size="small"
              />
            </Box>
          )}
        {!isDatabaseProvider && !hasPublisherInfo && (
          <Tooltip title="No source information available">
            <Box
              component="span"
              sx={{ display: "inline-flex", color: "grey.500" }}
            >
              <ImInfo size={15} />
            </Box>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );

  const openSidebar = () => {
    const collectionIds = (data.collectionMemberships || [])
      .map((membership) => membership.collectionID_lng)
      .filter(Boolean);

    dispatch(setSelectedBookmarkOption("notes"));
    dispatch(setLevel(""));
    dispatch(setCollectionSourceId(data.collectionSourceId));
    dispatch(
      setSummaryInfoData({
        publisher: data.entityName,
        title: data.name,
        published_date: data.datePublishedDisplayText,
        url: data.url,
        utc_date_published: data.datePublished,
        isDatabaseSearch: isDatabaseProvider,
        dbId: String(category).toLowerCase() === "ebsco" ? data.dbId ?? data.DbId : undefined,
        an: String(category).toLowerCase() === "ebsco" ? data.an ?? data.An : undefined,
      }),
    );
    dispatch(setCollectionId(data.collectionId ?? collectionIds[0]));
    dispatch(
      setBookmarkedCollectionIds(
        collectionIds.length > 0
          ? collectionIds
          : data.collectionId
          ? [data.collectionId]
          : []
      )
    );
    dispatch(setIsCitationAvailable(data.isCitationAvailable));
    dispatch(setIsSummaryAvailable(data.isSummaryAvailable));
    dispatch(setReadingLevelAvailable(data.isReadingLevelAvailable));
    dispatch(setIsSidebarOpen(true));
  };

  const newUrl = data.url.includes("foreignpolicy.com")
    ? data.url + "?auth=sooth"
    : data.url;

  const BookmarkOptions = () => (
    <Stack
      onClick={() => {
        openSidebar();
        logEvent("open_bookmark_popup", {
          url: newUrl,
          query,
          type: "generic",
          entity: data.entityName,
          category,
          mediabiasrating: data.mediaBiasRating || "NA",
          score: showNewsguard(data.score) ? data.score : "NA",
        });
      }}
      className="card-options"
      direction="row"
      justifyContent="flex-end"
      alignItems="center"
      gap={1}
      mt={isDatabaseProvider ? 0 : 1}
      sx={{
        cursor: "pointer",
        pl: 1,
        pr: 1,
        borderRadius: 1,
        transition: "all 0.1s ease",
        ...(isDatabaseProvider && {
          position: "absolute",
          right: 8,
          bottom: 8,
        }),
        "&:hover": {
          backgroundColor: theme.palette.secondary.main,
          "& .collection-hover": { opacity: "1 !important" },
        },
      }}
    >
      {data.collectionSourceId ? (
        <CollectionsLogo
          onClick={openSidebar}
          alt="Collections"
          style={{ width: 18, height: 18, fill: "blue", color: "blue" }}
        />
      ) : (
        <CollectionsLogoHover
          className="collection-hover"
          onClick={openSidebar}
          alt="Collections"
          style={{
            width: 17,
            height: 17,
            fill: "blue",
            color: "blue",
            opacity: 0,
            transition: "all 0.1s ease",
          }}
        />
      )}
      <AccessibleIconButton
        title="Open Bookmark"
        sx={{
          p: 0,
          "&:hover": {
            backgroundColor: "transparent",
          },
        }}
      >
        <MoreHorizIcon />
      </AccessibleIconButton>
    </Stack>
  );

  const ImageSection = ({ isDatabaseImage = false }) => {
    const imageUrl = data?.openGraphImage?.contentUrl;
    const [errored, setErrored] = useState(false);
    const databaseMediaSx = {
      position: "absolute",
      right: 16,
      bottom: 32,
      height: "6em",
      width: {
        xs: "calc(30% - 16px)",
        sm: "calc(45% - 16px)",
        md: "calc(30% - 16px)",
        lg: "calc(35% - 16px)",
      },
    };

    const onError = ({ currentTarget }) => {
      if (!errored) {
        setErrored(true);
        currentTarget.src = defaultBackground;
        currentTarget.alt = "Image not found";
      }
    };

    return (
      <Box
        sx={{
          flexGrow: isDatabaseImage ? 0 : 1,
          flexBasis: { xs: "30%", sm: "45%", md: "30%", lg: "35%" },
          display: "flex",
          flexDirection: "column",
          justifyContent: isDatabaseImage ? "flex-end" : "space-between",
          alignItems: "flex-end",
          height: isDatabaseImage ? "auto" : "100%",
          minHeight: isDatabaseImage ? "calc(6em + 24px)" : undefined,
        }}
      >
        {isDatabaseImage && isEbsco && imageUrl && !errored ? (
          <CardMedia
            component="img"
            sx={{
              ...databaseMediaSx,
              borderRadius: "10px",
              objectFit: "contain",
            }}
            image={imageUrl}
            alt={data.name || "EBSCO result"}
            onError={() => setErrored(true)}
          />
        ) : isDatabaseImage ? (
          <Box sx={{ ...databaseMediaSx, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", borderRadius: "10px" }}>
            {isEbsco ? (
              <EbscoLogo aria-label="EBSCO Information Services" style={{ width: "100%", height: "100%" }} />
            ) : (
              <Box title="ProQuest" role="img" aria-label="ProQuest" className="pq-header-logo"
                sx={{ width: "100%", height: "100%", filter: theme.palette.mode === "dark" ? "none" : "brightness(0)", backgroundImage: `url('${proQuestLogo}')`, backgroundSize: "contain", backgroundRepeat: "no-repeat", backgroundPosition: "center" }} />
            )}
          </Box>
        ) : (
          <CardMedia
            component="img"
            sx={{
              aspectRatio: 1,
              borderRadius: "10px",
              minHeight: "80px",
              width: "100%",
              objectFit: "cover",
              maxHeight: { xs: "6em", md: "unset" },
            }}
            image={imageUrl || defaultBackground}
            alt={imageUrl ? data.name : "Image not found"}
            onError={onError}
          />
        )}
        <BookmarkOptions />
      </Box>
    );
  };

  const swiper = useSwiper();

  useEffect(() => {
    function triggerSlideTo({ detail }) {
      console.log("swiping", slideIndex, swiper);
      swiper.slideTo(slideIndex);
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent(detail.action, { detail: detail.index }),
        );
      }, 0);
    }
    function triggerOpenSourcePanel({ detail }) {
      openSourceSidebar(true);
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("tour:app:sourcePanel:onLoad", { detail }),
        );
      }, 0);
    }
    window.addEventListener(
      `tour:app:article:slideTo:${hashId}`,
      triggerSlideTo,
    );
    window.addEventListener(
      `tour:app:article:sourceOpen:${hashId}`,
      triggerOpenSourcePanel,
    );
    return () => {
      window.removeEventListener(
        `tour:app:article:slideTo:${hashId}`,
        triggerSlideTo,
      );
      window.removeEventListener(
        `tour:app:article:sourceOpen:${hashId}`,
        triggerOpenSourcePanel,
      );
    };
  }, [hashId]);

  return (
    <Card
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        boxShadow: "none",
        ...(isDatabaseProvider && { boxSizing: "border-box" }),
        borderRadius: 1,
        p: 1,
        transition: `all ${theme.transitions.duration.standard}ms ${theme.transitions.easing.sharp}`,
        "&:hover": { backgroundColor: theme.palette.action.hover },
      }}
    >
      <Stack className={isDatabaseProvider ? "database-card-content" : undefined} direction="column" gap={isDatabaseProvider ? 0.75 : 1} justifyContent={isDatabaseProvider ? "flex-start" : "space-between"}>
        <Box sx={{ width: "100%" }}>
          <CardHeader />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
              p: "0 1rem",
              mt: isDatabaseProvider ? 0.75 : 1,
            }}
          >
            <Box
              sx={{
                maxHeight: isDatabaseProvider ? "none" : "5em",
                mb: isDatabaseProvider ? 0 : 2,
                minHeight: isDatabaseProvider ? 0 : "3em",
                display: "flex",
                alignItems: "flex-start",
                gap: 0.5,
              }}
            >
              <SearchResultLink
                url={data.url}
                trackingCat="click_resultset_title"
                trackingOptions={{
                  url: data.url,
                  query: query?.trim() ? query : undefined,
                  entity: data.entityName,
                  title: data.name,
                  category,
                  mediabiasrating: data.mediaBiasRating || "NA",
                  score: showNewsguard(data.score) ? data.score : "NA",
                  date_published: data.datePublished,
                }}
                variant="body4"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: isDatabaseProvider ? "visible" : "hidden",
                  textOverflow: "ellipsis",
                  display: isDatabaseProvider ? "block" : "-webkit-box",
                  WebkitLineClamp: 2,
                  overflowWrap: "anywhere",
                  WebkitBoxOrient: "vertical",
                  lineHeight: "1.5em",
                  cursor: "pointer",
                  "&:hover": { color: theme.palette.primary.light },
                }}
              >
                {isDatabaseProvider ? (
                  <Box component="span" sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    lineHeight: "1.5em",
                    pt: "2px",
                  }}>
                    {data.name}
                  </Box>
                ) : data.name}
              </SearchResultLink>
              {devViewPdf && (
                <FileTypeIcon
                  url={data.url}
                  sx={{ flexShrink: 0, mt: "2px" }}
                />
              )}
            </Box>
            {data.hasPayWall && data.isPartner ? (
              <SmileyIcon />
            ) : (
              data.hasPayWall && (
                <Tooltip
                  placement="bottom-start"
                  title="This source may limit your access without a subscription"
                >
                  <Box sx={{ cursor: "pointer" }}>
                    <HiOutlineLockClosed />
                  </Box>
                </Tooltip>
              )
            )}
          </Box>
        </Box>

        {isDatabaseProvider && (
          <Box sx={{ px: 2 }}>
            <DatabaseDetails />
          </Box>
        )}
        <Box sx={{ display: "flex", alignItems: "stretch", px: 1, mt: isDatabaseProvider ? 1.5 : 0 }}>
          <Stack
            sx={{
              width: "100%",
              flexGrow: 1,
              flexBasis: { xs: "70%", sm: "50%", md: "50%", lg: "65%" },
              pr: 2,
              flexShrink: 0,
            }}
          >
            <Typography
              variant={isMobile ? "body4" : "body1"}
              sx={{
                color: isDatabaseProvider ? "text.secondary" : "grey.700",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: isDatabaseProvider ? 3 : { xs: 4, md: 4, lg: 5 },
                WebkitBoxOrient: "vertical",
                maxHeight: isDatabaseProvider ? "4.5em" : { xs: "6em", md: "6em", lg: "7.5em" },
                lineHeight: "1.5em",
                minHeight: isDatabaseProvider ? 0 : "4.5em",
                wordBreak: "break-word",
              }}
            >
              {(isDatabaseProvider || !isMobile) && data.datePublishedDisplayText
                ? `${data.datePublishedDisplayText}${data.snippet ? " - " : ""}`
                : ""}
              {data.snippet}
            </Typography>
          </Stack>
          <ImageSection isDatabaseImage={isDatabaseProvider} />
        </Box>
      </Stack>
    </Card>
  );
};

export const NewsCard = memo(NewsCardComponent);
